import React, { useState, useEffect, useCallback, useRef } from 'react';
import { analyzeATSWithAI, logEvent, updateResumeData, convertHtmlToPdf, submitResumeFeedback } from '../../../../services/resume.service';
import { HOST } from '../../../../services/server';
import { toast } from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import CreditBadge from '../common/CreditBadge';
import ResumeEditor from '../common/ResumeEditor';
import { getTemplateById } from '../../../../utils/resumeTemplates';

// ─────────────────────────────────────────────────────────────────
// Helper: build text summary from resumeData for ATS analysis
// ─────────────────────────────────────────────────────────────────
const buildATSText = (rd) => {
  if (!rd) return '';
  const lines = [];
  if (rd.summary) lines.push(`SUMMARY:\n${rd.summary}`);

  if (rd.experience?.length) {
    lines.push('EXPERIENCE:');
    rd.experience.forEach(exp => {
      lines.push(`  ${exp.position || ''} at ${exp.company || ''} (${exp.dates || exp.duration || ''})`);
      (exp.bullets || exp.responsibilities || []).forEach(b => lines.push(`    - ${b}`));
    });
  }

  if (rd.projects?.length) {
    lines.push('PROJECTS:');
    rd.projects.forEach(item => {
      lines.push(`  ${item.name || item.title || item.position || ''}`);
      if (item.description) lines.push(`    ${item.description}`);
      (item.bullets || item.responsibilities || []).forEach(b => lines.push(`    - ${b}`));
    });
  }

  if (rd.leadership?.length) {
    lines.push('LEADERSHIP / PROJECTS:');
    rd.leadership.forEach(item => {
      lines.push(`  ${item.title || item.position || item.name || ''}`);
      if (item.description) lines.push(`    ${item.description}`);
      (item.bullets || item.responsibilities || []).forEach(b => lines.push(`    - ${b}`));
    });
  }

  if (rd.education?.length) {
    lines.push('EDUCATION:');
    rd.education.forEach(edu => lines.push(`  ${edu.degree || ''} — ${edu.institution || edu.university || ''}`));
  }

  if (rd.customSections?.length) {
    rd.customSections.forEach(sec => {
      lines.push(`${(sec.sectionTitle || sec.title || '').toUpperCase()}:`);
      (sec.items || []).forEach(item => {
        const name = typeof item === 'string' ? item : (item.name || item.title || '');
        if (name) lines.push(`  ${name}`);
        (item.bullets || []).forEach(b => lines.push(`    - ${b}`));
        if (item.description) lines.push(`    ${item.description}`);
      });
    });
  }

  if (rd.skills) {
    const st = Array.isArray(rd.skills)
      ? rd.skills.map(s => typeof s === 'string' ? s : `${s.category || ''}: ${Array.isArray(s.items) ? s.items.join(', ') : (s.items || '')}`).join(' | ')
      : String(rd.skills);
    lines.push(`SKILLS: ${st}`);
  }
  return lines.join('\n');
};

// ─────────────────────────────────────────────────────────────────
// Main PreviewStep
// ─────────────────────────────────────────────────────────────────
// Module-level constant so it's accessible everywhere in the component
const selectedTemplate = getTemplateById('classic-ats');

const PreviewStep = ({ onNavigate, onDataUpdate, result: initialResult, targetRole, gapAnalysis, jobDescription, uploadedFile, reviewedData }) => {
  const [result, setResult] = useState(initialResult);

  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  // Tracks in-progress edits inside the editor modal before the user hits Save
  const [liveEditorData, setLiveEditorData] = useState(null);
  // 'html' = live HTML template preview; 'pdf' = iframe PDF preview
  const [previewMode, setPreviewMode] = useState('html');

  const [atsAnalysis, setAtsAnalysis] = useState(null);
  const [atsLoading, setAtsLoading] = useState(!!result);
  const [atsError, setAtsError] = useState(null);
  const [atsVersion, setAtsVersion] = useState(0);

  // Feedback State
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackFeel, setFeedbackFeel] = useState('');
  const [feedbackSections, setFeedbackSections] = useState('');
  const [feedbackStars, setFeedbackStars] = useState(0);
  const [feedbackNotes, setFeedbackNotes] = useState('');

  const [hasClickedActionBefore, setHasClickedActionBefore] = useState(false);
  const [feedbackBlinking, setFeedbackBlinking] = useState(false);
  const feedbackRef = useRef(null);

  const resumeId = result?.id || result?.resume_id || initialResult?.id || initialResult?.resume_id || uploadedFile?.id || reviewedData?.id;
  const storageKey = `feedback_FEEDBACK_STEP_5_PREVIEW_${resumeId}`;

  useEffect(() => {
    if (resumeId && localStorage.getItem(storageKey)) {
      setFeedbackSubmitted(true);
    }
  }, [resumeId, storageKey]);

  // Post-download Modal Feedback State
  const [showDownloadFeedbackModal, setShowDownloadFeedbackModal] = useState(false);
  const [postDownloadSubmitted, setPostDownloadSubmitted] = useState(false);
  const [postDownloadReady, setPostDownloadReady] = useState('');
  const [postDownloadIssues, setPostDownloadIssues] = useState([]);
  const [postDownloadOtherIssue, setPostDownloadOtherIssue] = useState('');
  const [postDownloadStars, setPostDownloadStars] = useState(0);
  const [postDownloadNotes, setPostDownloadNotes] = useState('');

  const handleFeedbackSubmit = async () => {
    if (!feedbackFeel || !feedbackSections) {
      toast.error('Please answer the multiple-choice questions');
      return false;
    }
    if (feedbackStars === 0) {
      toast.error('Please rate your overall experience');
      return false;
    }
    try {
      const userId = localStorage.getItem('user_id');
      const category = localStorage.getItem('category');
      await submitResumeFeedback({
        user_id: userId,
        category: category,
        resume_id: resumeId || null,
        step: 'FEEDBACK_STEP_5_PREVIEW',
        feedback_data: {
          feelsLikeYou: feedbackFeel,
          sectionsMadeIt: feedbackSections,
          stars: feedbackStars,
          notes: feedbackNotes
        }
      });
      setFeedbackSubmitted(true);
      if (resumeId) {
        localStorage.setItem(storageKey, 'true');
      }
      toast.success('Thank you for your feedback!');
      return true;
    } catch (error) {
      toast.error('Failed to submit feedback');
      return false;
    }
  };

  const handlePostDownloadSubmit = async () => {
    if (!postDownloadReady) return toast.error('Please select an option');
    try {
      const userId = localStorage.getItem('user_id');
      const category = localStorage.getItem('category');
      await submitResumeFeedback({
        user_id: userId,
        category: category,
        resume_id: result?.id || result?.resume_id || null,
        step: 'FEEDBACK_POST_DOWNLOAD',
        feedback_data: {
          readyToSend: postDownloadReady,
          issues: postDownloadIssues,
          otherIssue: postDownloadOtherIssue,
          stars: postDownloadStars,
          notes: postDownloadNotes
        }
      });
      setPostDownloadSubmitted(true);
      setTimeout(() => setShowDownloadFeedbackModal(false), 2000);
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const [previewKey, setPreviewKey] = useState(0);

  // ────────────────────────────────────────────────
  // DIAGNOSTIC LOGGING on mount
  // ────────────────────────────────────────────────
  useEffect(() => {
    console.group('[PreviewStep] Mount Diagnostics');
    console.log('initialResult:', initialResult);
    console.log('initialResult.downloadUrl:', initialResult?.downloadUrl);
    console.log('initialResult.resumeData:', initialResult?.resumeData);
    console.log('initialResult.resumeData?.name:', initialResult?.resumeData?.name);
    console.log('initialResult.resumeData?.experience?.length:', initialResult?.resumeData?.experience?.length);
    console.log('uploadedFile:', uploadedFile);
    console.log('uploadedFile.updated_resume_url:', uploadedFile?.updated_resume_url);
    console.groupEnd();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ────────────────────────────────────────────────
  // FALLBACK: If downloadUrl is empty but resumeData exists,
  // generate an HTML blob URL on the fly so the preview is never blank.
  // This handles old DB records that don't have updated_resume_url set yet.
  // ────────────────────────────────────────────────
  useEffect(() => {
    if (!result?.downloadUrl && result?.resumeData) {
      console.log('[PreviewStep] downloadUrl empty — generating HTML blob fallback for preview...');
      try {
        const html = selectedTemplate.generateHTML(result.resumeData);
        const blob = new Blob([html], { type: 'text/html' });
        const blobUrl = URL.createObjectURL(blob);
        console.log('[PreviewStep] HTML blob URL created:', blobUrl);
        setResult(prev => ({ ...prev, downloadUrl: blobUrl }));
        setPreviewKey(k => k + 1);
      } catch (err) {
        console.error('[PreviewStep] Failed to generate HTML fallback blob:', err);
      }
    } else {
      console.log('[PreviewStep] downloadUrl present:', result?.downloadUrl);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getPreviewUrl = useCallback(() => {
    if (!result?.downloadUrl) return '';
    if (result.downloadUrl.startsWith('blob:')) return result.downloadUrl;
    if (result.downloadUrl.startsWith('http')) return result.downloadUrl;
    return `${HOST}${result.downloadUrl}`;
  }, [result]);

  // ── Editor handlers ──────────────────────────────────────────────

  /** Called on every keystroke in the editor — updates the live preview */
  const handleEditorChange = (updatedData) => {
    setLiveEditorData(updatedData);
  };

  /** Discard button — close the modal without saving */
  const handleCancelEditing = () => {
    setLiveEditorData(null);
    setPreviewMode('html');
    setIsEditing(false);
  };

  // Compute the live HTML for the right-panel preview inside the editor.
  // Falls back to an empty string if there is no data yet.
  const htmlPreviewContent = (() => {
    try {
      const editorData = liveEditorData || result?.resumeData;
      if (!editorData) return '';
      return selectedTemplate.generateHTML(editorData);
    } catch {
      return '';
    }
  })();

  // ── Save ──────────────────────────────────────────────────────────

  const handleSave = async (editedData) => {
    // If called from the Save button in the modal the data is liveEditorData;
    // if called programmatically the caller can pass explicit data.
    const dataToSave = editedData || liveEditorData || result?.resumeData;
    setSaving(true);

    try {
      toast.loading('Saving and updating preview…', { id: 'regen' });

      // ─── FIX Bug 1: Resolve the DB row ID from all available sources ───
      // The create flow now attaches uploadedFile.id to result.id (fixed in GapAnalysisStep /
      // ExperienceBuilderStep), but we keep the full fallback chain for safety.
      const resumeId =
        result?.id ||
        result?.resume_id ||
        initialResult?.id ||
        initialResult?.resume_id ||
        uploadedFile?.id ||
        reviewedData?.id;

      if (!resumeId) {
        console.warn('[PreviewStep] Could not resolve resumeId — DB update will be skipped. Check that uploadedFile.id is passed when navigating to preview.');
      }

      if (resumeId) {
        await updateResumeData(resumeId, {
          ...dataToSave,
          step: 'preview',
          targetRole,
          jobDescription
        });
      }

      // 2. Generate local HTML & Convert to Blob (Instant UI Update)
      const htmlContent = selectedTemplate.generateHTML(dataToSave);
      let objectUrl = '';
      try {
        const blob = await convertHtmlToPdf(htmlContent, 'resume.pdf');
        objectUrl = URL.createObjectURL(blob);
      } catch (err) {
        console.warn('Instant UI update PDF failed, relying on background regeneration', err);
        objectUrl = result?.downloadUrl || '';
      }

      const updatedResult = {
        ...result,
        resumeData: dataToSave,
        downloadUrl: objectUrl
      };

      setResult(updatedResult);
      onDataUpdate({
        ...dataToSave,
        result: updatedResult
      });

      setPreviewKey(k => k + 1);
      setAtsVersion(v => v + 1);
      setLiveEditorData(null);
      setPreviewMode('html');
      setIsEditing(false);

      toast.success('Resume updated!', { id: 'regen' });
      logEvent('RESUME_EDITED', { type: 'structured_editor' });

      // 3. Background Sync to GCP (The Hybrid Approach)
      const token = localStorage.getItem('sso_token') || localStorage.getItem('token');
      const sid = localStorage.getItem('session_id');
      const hdrs = { 'Content-Type': 'application/json' };
      if (token) hdrs['Authorization'] = `Bearer ${token}`;
      if (sid) hdrs['x-session-id'] = sid;

      // Failsafe: Prevent production from hitting localhost if env variable is wrong
      const apiHost = HOST?.includes('localhost') && window.location.hostname !== 'localhost'
        ? ''
        : (HOST || '');

      fetch(`${apiHost}/resume/regenerate`, {
        method: 'POST',
        headers: hdrs,
        body: JSON.stringify({ resumeData: dataToSave })
      })
        .then(res => res.json())
        .then(data => {
          if (data.success && data.downloadUrl) {
            console.log('✅ Background GCS Sync Complete:', data.downloadUrl);

            // Append cache buster to force browser to reload the full backend PDF
            const busterUrl = data.downloadUrl + (data.downloadUrl.includes('?') ? '&' : '?') + 't=' + Date.now();

            // Silently patch the global state with the permanent GCS link
            const permanentResult = { ...updatedResult, downloadUrl: busterUrl, fileName: data.fileName };

            // Update the local state so the iframe re-renders with the real full-fidelity PDF
            setResult(permanentResult);

            onDataUpdate({ result: permanentResult });
            // Also persist the permanent URL to the DB so session restores work
            if (resumeId) {
              updateResumeData(resumeId, { ...dataToSave, step: 'preview', targetRole, jobDescription }, data.downloadUrl)
                .catch(err => console.warn('Failed to persist GCS URL to DB:', err));
            }
          }
        })
        .catch(err => console.error('Background GCS Sync Failed:', err));

    } catch (err) {
      console.error('Save/Regenerate Error:', err);
      toast.error(`Failed to update: ${err.message}`, { id: 'regen' });
    } finally {
      setSaving(false);
    }
  };

  const exportPdfMutation = useMutation({
    mutationFn: async () => {
      const htmlContent = selectedTemplate.generateHTML(result.resumeData);

      let baseFileName = uploadedFile?.file?.originalName || uploadedFile?.file?.filename || uploadedFile?.filename || uploadedFile?.resume_filename || result?.fileName || `resume.pdf`;
      baseFileName = baseFileName.split('/').pop();
      if (baseFileName.toLowerCase().endsWith('.pdf') || baseFileName.toLowerCase().endsWith('.docx') || baseFileName.toLowerCase().endsWith('.doc')) {
        baseFileName = baseFileName.substring(0, baseFileName.lastIndexOf('.'));
      }
      if (baseFileName.length > 30) baseFileName = baseFileName.substring(0, 30);

      const finalFileName = `${baseFileName}_updated_${Date.now()}.pdf`;
      const blob = await convertHtmlToPdf(htmlContent, finalFileName);

      return { blob, finalFileName };
    },
    onSuccess: ({ blob, finalFileName }) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalFileName;
      a.click();
      window.URL.revokeObjectURL(url);
      logEvent('PDF_DOWNLOADED', { type: 'instant_download' });
      toast.success('Downloaded!', { id: 'pdf' });
      setShowDownloadFeedbackModal(true);
    },
    onError: (err) => {
      console.error('PDF Export Error:', err);
      toast.error('Failed to export PDF. Please try again.', { id: 'pdf' });
    }
  });

  const handleDownload = async () => {
    if (!feedbackSubmitted) {
      if (feedbackFeel && feedbackSections && feedbackStars > 0) {
        const success = await handleFeedbackSubmit();
        if (!success) return;
      } else {
        toast.error('Please complete the feedback section or click "Submit Feedback".');
        if (feedbackRef.current) {
          feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setFeedbackBlinking(true);
          setTimeout(() => setFeedbackBlinking(false), 2000);
        }
        return;
      }
    }
    toast.loading('Preparing download...', { id: 'pdf' });
    exportPdfMutation.mutate();
  };

  const handleEdit = async () => {
    if (!feedbackSubmitted) {
      if (feedbackFeel && feedbackSections && feedbackStars > 0) {
        const success = await handleFeedbackSubmit();
        if (!success) return;
      } else {
        toast.error('Please complete the feedback section or click "Submit Feedback".');
        if (feedbackRef.current) {
          feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setFeedbackBlinking(true);
          setTimeout(() => setFeedbackBlinking(false), 2000);
        }
        return;
      }
    }
    setIsEditing(true);
  };

  useEffect(() => {
    if (!result || isEditing) return;
    const runATS = async () => {
      const rd = result?.resumeData;
      if (!rd || !targetRole) { setAtsLoading(false); return; }
      setAtsLoading(true);
      setAtsError(null);

      try {
        const userId = localStorage.getItem('user_id');
        const category = localStorage.getItem('category');
        const knownBefore = gapAnalysis?.atsScore || gapAnalysis?.matchScore || null;

        const analysisText = buildATSText(rd);
        const analysis = await analyzeATSWithAI(analysisText, targetRole, userId, category);

        if (analysis) {
          const beforeScore = knownBefore || analysis.beforeScore || null;
          const afterScore = analysis.afterScore || gapAnalysis?.atsScore || 0;
          setAtsAnalysis({
            ...analysis,
            afterScore,
            beforeScore,
            improvement: (beforeScore !== null && afterScore !== null) ? afterScore - beforeScore : (analysis.improvement || 0),
            foundTechnical: analysis.foundKeywords?.technical || analysis.foundTechnical || [],
            foundImpact: analysis.foundKeywords?.impact || analysis.foundImpact || [],
            foundVerbs: analysis.foundKeywords?.actionVerbs || analysis.foundVerbs || [],
            missingTechnical: analysis.missingKeywords?.technical || analysis.missingTechnical || gapAnalysis?.missingSkills || [],
            missingImpact: analysis.missingKeywords?.impact || analysis.missingImpact || [],
          });
          window.dispatchEvent(new CustomEvent('credits-updated'));
        }
      } catch (err) {
        setAtsError('ATS analysis failed. Your resume was still generated successfully.');
      } finally {
        setAtsLoading(false);
      }
    };
    runATS();
  }, [result, targetRole, isEditing, gapAnalysis, atsVersion]);

  const currentAtsScore = atsAnalysis?.afterScore || gapAnalysis?.atsScore || 0;
  const missingKeywords = atsAnalysis?.missingTechnical?.length > 0
    ? atsAnalysis.missingTechnical
    : (gapAnalysis?.missingSkills || []);
  const foundKeywords = atsAnalysis?.foundTechnical || [];

  return (
    <div className="bg-slate-50 text-slate-800 font-inter flex flex-col min-h-screen">
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-[100]">
        <div className="max-w-[1200px] mx-auto px-8 h-[68px] flex items-center justify-between">
          <div className="flex items-center gap-[10px] cursor-pointer" onClick={() => onNavigate('dashboard')}>
            <button
              type="button"
              className="flex items-center gap-2 px-4 h-10 bg-white border border-slate-200 rounded-xl text-[14px] font-bold text-slate-600 hover:text-navy hover:bg-slate-50 hover:border-slate-300 transition-all shadow-sm whitespace-nowrap"
            >
              <span className="msi text-[18px]">arrow_back</span>
              Back to Dashboard
            </button>
          </div>
          <div className="flex items-center gap-6">
            <CreditBadge />
            <div className="flex items-center gap-3">
              {[{ n: 1, l: 'Upload', s: 'green' }, { n: 2, l: 'Review', s: 'green' }, { n: 3, l: 'Target', s: 'green' }, { n: 4, l: 'Finalize', s: 'blue' }].map((step, i) => (
                <React.Fragment key={step.n}>
                  <div className={`flex items-center gap-2 text-[13px] font-semibold text-premium-${step.s}`}>
                    <div className={`w-6 h-6 rounded-full ${step.s === 'green' ? 'bg-premium-green text-white' : 'border-2 border-blue-500 bg-blue-50 text-blue-500'} flex items-center justify-center text-[11px]`}>{step.n}</div>
                    <span className="hidden sm:inline">{step.l}</span>
                  </div>
                  {i < 3 && <div className="w-6 h-[2px] bg-slate-200" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-8 w-full flex-1 flex flex-col">
        <div className="inline-flex items-center gap-1.5 text-slate-400 text-[13px] font-semibold cursor-pointer hover:text-blue-500 mb-4 hover:underline underline-offset-4" onClick={() => onNavigate(-1)}>
          <span className="msi">arrow_back</span> Back
        </div>

        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 animate-fade-up">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-50 text-green-600 flex items-center justify-center shrink-0 border border-green-100">
              <span className="msi text-[28px]">check_circle</span>
            </div>
            <div>
              <h1 className="text-[28px] font-extrabold text-navy tracking-[-0.8px] mb-1 font-montserrat leading-tight">Your Resume is Ready</h1>
              <p className="text-[14px] text-slate-500 font-inter">
                Optimized for {targetRole || 'the new domain'}. Review your ATS score below.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={handleEdit}
              className="h-11 px-5 font-bold rounded-xl text-[13px] flex items-center gap-2 transition-all bg-white text-navy border border-slate-200 hover:bg-slate-50"
            >
              <span className="msi text-[18px]">edit_note</span> Edit Resume
            </button>
            <button
              className={`h-11 px-6 font-bold rounded-xl text-[13px] flex items-center gap-2 transition-all ${exportPdfMutation.isPending ? 'bg-slate-100 text-slate-400' : 'bg-blue-500 text-white shadow-md shadow-blue-500/20 hover:bg-blue-600'}`}
              onClick={handleDownload}
              disabled={exportPdfMutation.isPending}
            >
              <span className="msi text-[18px]">{exportPdfMutation.isPending ? 'sync' : 'download'}</span>
              {exportPdfMutation.isPending ? 'Processing...' : 'Export PDF'}
            </button>
          </div>
        </div>

        {!feedbackSubmitted ? (
          <div
            ref={feedbackRef}
            className={`bg-white border border-slate-200 rounded-3xl p-6 shadow-sm mb-8 animate-fade-up transition-all duration-300 ${feedbackBlinking ? 'ring-4 ring-blue-500/50 scale-[1.02]' : ''}`}
          >
            <h3 className="text-[15px] font-bold text-navy mb-4">Final Check</h3>

            <div className="mb-4">
              <p className="text-[13px] font-semibold text-slate-700 mb-2">How was your overall experience?</p>
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setFeedbackStars(star)}
                    className={`msi text-[28px] transition-colors ${star <= feedbackStars ? 'text-amber-400' : 'text-slate-300 hover:text-amber-200'}`}
                  >
                    {star <= feedbackStars ? 'star' : 'star_border'}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-4">
              <div>
                <p className="text-[13px] font-semibold text-slate-700 mb-2">Does this resume feel like YOU?</p>
                <div className="flex flex-wrap gap-2">
                  {['🙌 Absolutely', '😐 Somewhat', '😕 Not really'].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setFeedbackFeel(opt)}
                      className={`px-4 py-2 rounded-xl text-[13px] font-semibold transition-all border ${feedbackFeel === opt ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-[13px] font-semibold text-slate-700 mb-2">Did all your original sections make it to the final resume?</p>
                <div className="flex flex-wrap gap-2">
                  {['✅ Yes, all good', '⚠️ Some are missing', '❌ A lot is missing'].map(opt => (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => setFeedbackSections(opt)}
                      className={`px-4 py-2 rounded-xl text-[13px] font-semibold transition-all border ${feedbackSections === opt ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-4">
              <label className="text-[13px] font-semibold text-slate-700 mb-2 block">One thing you'd change about this resume?</label>
              <input
                type="text"
                placeholder="Tell us more..."
                value={feedbackNotes}
                onChange={(e) => setFeedbackNotes(e.target.value)}
                className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleFeedbackSubmit}
              disabled={!feedbackFeel || !feedbackSections || feedbackStars === 0}
              className="px-5 py-2 bg-blue-500 text-white rounded-xl text-[13px] font-bold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit Feedback
            </button>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-8 flex items-center gap-3">
            <span className="msi text-green-600 text-[20px]">check_circle</span>
            <p className="text-[13.5px] font-semibold text-green-800">Thank you for your feedback! It helps us improve.</p>
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-8 items-start">
          <div className="flex-1 w-full min-w-0 bg-white rounded-2xl border border-slate-200 shadow-sm animate-fade-up">
            <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex flex-wrap justify-between items-center gap-2 rounded-t-2xl">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-extrabold text-slate-400 uppercase tracking-widest font-montserrat">Live Preview</span>
                <span className="inline-flex items-center gap-1.5 bg-green-100 text-green-700 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div> Auto-saved
                </span>
                <span className="inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border border-slate-200 bg-slate-100 text-slate-500">
                  <span className="msi text-[11px]">description</span> Classic ATS
                </span>
              </div>
              <button onClick={() => setIsEditing(true)} className="inline-flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-full bg-blue-500/10 text-blue-600 border border-blue-500/20 hover:bg-blue-500/20 transition-all cursor-pointer">
                <span className="msi text-[12px]">edit</span> Edit Content
              </button>
            </div>

            <div className="bg-slate-100 rounded-b-2xl">
              <div className="p-6 lg:p-10 flex justify-center">
                <div className="w-full max-w-[816px] bg-white shadow-2xl h-[1056px] overflow-y-auto custom-scrollbar">
                  {/* Smart preview: use inline HTML when we have resumeData, iframe only for GCS PDF URLs */}
                  {getPreviewUrl() ? (
                    <iframe
                      key={previewKey}
                      src={getPreviewUrl()}
                      title="Resume Preview"
                      className="w-full block"
                      style={{ border: 'none', height: '100%' }}
                    />
                  ) : result?.resumeData ? (
                    <div
                      key={previewKey}
                      className="p-[56px_64px] preview-content-wrapper"
                      dangerouslySetInnerHTML={{
                        __html: (() => {
                          try { return selectedTemplate.generateHTML(result.resumeData); }
                          catch { return '<p style="padding:40px;color:#888">Preview unavailable</p>'; }
                        })()
                      }}
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-4 text-slate-400">
                      <span className="msi text-[48px] opacity-40">description</span>
                      <p className="text-[14px] font-medium">Preview not available</p>
                      <p className="text-[12px] text-slate-300">Try downloading the PDF directly</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full lg:w-[400px] shrink-0 space-y-6 animate-fade-up" style={{ animationDelay: '0.1s' }}>
            <div className="p-6 border border-slate-200 shadow-sm rounded-2xl bg-white">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <span className="msi text-blue-500 text-[20px]">track_changes</span>
                  <h3 className="text-[15px] font-bold text-navy font-montserrat">Target Job Analysis</h3>
                </div>
                {currentAtsScore > 0 && !atsLoading && (
                  <span className={`text-[12px] font-bold px-2.5 py-1 rounded-lg ${currentAtsScore >= 80 ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {currentAtsScore}% Match
                  </span>
                )}
              </div>

              {atsLoading ? (
                <div className="flex flex-col items-center justify-center py-8 text-center">
                  <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-500 rounded-full animate-spin mb-3"></div>
                  <p className="text-[13px] text-slate-500 font-medium">Recalculating score...</p>
                </div>
              ) : (
                <>
                  <div className="mb-5">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-[13px] font-bold text-slate-700 truncate" title={targetRole}>{targetRole || 'General Role'}</p>
                      {atsAnalysis?.improvement > 0 && <p className="text-[11px] text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded-md">+{atsAnalysis.improvement}% Boost</p>}
                    </div>
                    <div className="h-2.5 bg-slate-100 rounded-full w-full overflow-hidden relative">
                      <div className={`h-full absolute left-0 top-0 transition-all duration-1000 ${currentAtsScore >= 80 ? 'bg-green-500' : 'bg-blue-500'}`} style={{ width: `${currentAtsScore}%` }}></div>
                      {atsAnalysis?.beforeScore != null && <div className="absolute top-0 bottom-0 w-1 bg-white z-10 opacity-80" style={{ left: `${atsAnalysis.beforeScore}%` }} title={`Original: ${atsAnalysis.beforeScore}%`}></div>}
                    </div>
                  </div>

                  {atsAnalysis?.summary && (
                    <p className="text-[12.5px] text-slate-500 leading-relaxed font-inter bg-slate-50 p-3.5 rounded-xl border border-slate-100">
                      {atsAnalysis.summary}
                    </p>
                  )}
                  {atsError && !atsAnalysis?.summary && (
                    <p className="text-[13px] text-red-500">{atsError}</p>
                  )}
                </>
              )}
            </div>

            <div className="p-6 border border-slate-200 shadow-sm rounded-2xl bg-white">
              <div className="flex items-center gap-2 mb-5">
                <span className="msi text-blue-500 text-[20px]">vpn_key</span>
                <h3 className="text-[15px] font-bold text-navy font-montserrat">Keywords</h3>
              </div>
              {atsLoading ? (
                <div className="animate-pulse flex flex-wrap gap-2">
                  {[16, 24, 20].map(w => <div key={w} className="h-6 bg-slate-100 rounded" style={{ width: `${w * 4}px` }}></div>)}
                </div>
              ) : (foundKeywords.length > 0 || missingKeywords.length > 0) ? (
                <div className="space-y-4">
                  {foundKeywords.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {foundKeywords.slice(0, 10).map(kw => (
                        <div key={kw} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50/50 text-green-700 text-[11px] font-bold rounded-full border border-green-200 capitalize shadow-sm hover:bg-green-50 transition-all">
                          <span className="msi text-[13px]">check</span> {kw}
                        </div>
                      ))}
                    </div>
                  )}
                  {missingKeywords.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100 mt-2">
                      {missingKeywords.slice(0, 10).map(kw => (
                        <div key={kw} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50/30 text-red-600 text-[11px] font-bold rounded-full border border-red-200/60 capitalize shadow-sm hover:bg-red-50 transition-all">
                          <span className="msi text-[13px] opacity-70">add</span> {kw}
                        </div>
                      ))}
                      <p className="w-full text-[10.5px] text-slate-400 italic mt-1 text-center">Click Edit Document to incorporate missing keywords.</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[13px] text-slate-400">Not available.</p>
              )}
            </div>

            <div className="p-6 border border-blue-100 bg-blue-50/50 shadow-sm rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center shrink-0">
                  <span className="msi text-[22px]">auto_awesome</span>
                </div>
                <div>
                  <h3 className="text-[14px] font-bold text-navy font-montserrat mb-1">Edit & Improve</h3>
                  <p className="text-[12px] text-slate-500 font-inter leading-relaxed mb-4">
                    Open the structured editor to refine bullet points, add missing keywords, or adjust any section. Your PDF will be regenerated and ATS score recalculated automatically.
                  </p>
                  <button className="w-full bg-white font-bold border border-blue-200 hover:border-blue-500 text-blue-600 rounded-xl text-[13px] h-10 transition-colors" onClick={handleEdit}>
                    Open Editor
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {isEditing && (
        <ResumeEditor
          resumeData={result?.resumeData}
          onSave={handleSave}
          onCancel={handleCancelEditing}
          saving={saving}
        />
      )}

      {showDownloadFeedbackModal && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-[24px] p-8 max-w-[500px] w-full shadow-2xl relative animate-fade-up">
            <button
              onClick={() => setShowDownloadFeedbackModal(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
            >
              <span className="msi">close</span>
            </button>

            {!postDownloadSubmitted ? (
              <>
                <div className="w-12 h-12 bg-green-50 text-green-500 rounded-2xl flex items-center justify-center mb-6">
                  <span className="msi text-[24px]">task_alt</span>
                </div>
                <h2 className="text-[20px] font-extrabold text-navy font-montserrat mb-2">Resume Downloaded!</h2>
                <p className="text-[14px] text-slate-500 font-inter mb-6">Before you go, we'd love to know how you feel about your new resume.</p>

                <div className="mb-4">
                  <p className="text-[13px] font-bold text-navy mb-3 block">How was your overall experience?</p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setPostDownloadStars(star)}
                        className={`msi text-[28px] transition-colors ${star <= postDownloadStars ? 'text-amber-400' : 'text-slate-300 hover:text-amber-200'}`}
                      >
                        {star <= postDownloadStars ? 'star' : 'star_border'}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="text-[13px] font-bold text-navy mb-3 block">Would you confidently send this to a recruiter?</label>
                    <div className="flex flex-wrap gap-2">
                      {['🚀 Yes, it\'s ready', '🔧 Needs a few tweaks', '❌ Not yet'].map(opt => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setPostDownloadReady(opt)}
                          className={`px-4 py-2 rounded-xl text-[13px] font-semibold transition-all border ${postDownloadReady === opt ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-4">
                    <label className="text-[13px] font-bold text-navy mb-3 block">Anything the AI missed about your profile? (Optional)</label>
                    <input
                      type="text"
                      placeholder="Tell us more..."
                      value={postDownloadNotes}
                      onChange={(e) => setPostDownloadNotes(e.target.value)}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  {postDownloadReady && postDownloadReady !== '🚀 Yes, it\'s ready' && (
                    <div>
                      <label className="text-[13px] font-bold text-navy mb-3 block">What's holding you back?</label>
                      <div className="flex flex-wrap gap-2">
                        {['Formatting looks off', 'Content doesn\'t sound like me', 'Missing sections', 'ATS score too low', 'Tool added unnecessary details', 'Other'].map(issue => (
                          <button
                            key={issue}
                            type="button"
                            onClick={() => {
                              setPostDownloadIssues(prev =>
                                prev.includes(issue) ? prev.filter(i => i !== issue) : [...prev, issue]
                              )
                            }}
                            className={`px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all border ${postDownloadIssues.includes(issue) ? 'bg-amber-50 border-amber-500 text-amber-700' : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}`}
                          >
                            {issue}
                          </button>
                        ))}
                      </div>
                      {postDownloadIssues.includes('Other') && (
                        <div className="mt-4 animate-fade-up" style={{ animationDuration: '0.3s' }}>
                          <input
                            type="text"
                            placeholder="Please specify..."
                            value={postDownloadOtherIssue}
                            onChange={(e) => setPostDownloadOtherIssue(e.target.value)}
                            className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl font-inter text-sm focus:outline-none focus:border-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="mt-8 pt-6 border-t border-slate-100 flex justify-end gap-3">
                  <button
                    onClick={() => setShowDownloadFeedbackModal(false)}
                    className="px-5 h-[42px] rounded-xl text-[13px] font-bold text-slate-500 hover:bg-slate-50 transition-colors"
                  >
                    Skip
                  </button>
                  <button
                    onClick={handlePostDownloadSubmit}
                    disabled={!postDownloadReady}
                    className="px-6 h-[42px] rounded-xl text-[13px] font-bold bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    Submit
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-50 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="msi text-[32px]">favorite</span>
                </div>
                <h2 className="text-[20px] font-extrabold text-navy font-montserrat mb-2">Thank You!</h2>
                <p className="text-[14px] text-slate-500 font-inter">Your feedback helps us make Accredian AI better for everyone.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PreviewStep;